import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Editor from 'components/editor/Editor';
import { calculatePriority, getPriorityProps } from 'helpers/utils';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Button, Col, Dropdown, Form, Modal, Row } from 'react-bootstrap';
import TypeTicketsService from 'services/TypeTickets.service';

const CustomSelectToggle = forwardRef(({ children, onClick }, ref) => (
  <div
    ref={ref}
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
    className="form-select cursor-pointer"
  >
    {children}
  </div>
));

const SubcategoryEditModal = ({
  data,
  showModal,
  handleClose,
  handleSubmit
}) => {
  const [body, setBody] = useState({
    name: '',
    inactive: false
  });
  const [ticketTypes, setTicketTypes] = useState([]);
  const [defaultSubject, setDefaultSubject] = useState('');
  const [defaultDescription, setDefaultDescription] = useState('');
  const [selectedTypeTicket, setSelectedTypeTicket] = useState('');
  const [ticketUrgency, setTicketUrgency] = useState('LOW');
  const [ticketImpact, setTicketImpact] = useState('LOW');
  const [ticketPriority, setTicketPriority] = useState('LOW');

  const form = useRef(null);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    const getTypeTickets = async () => {
      try {
        const response = await TypeTicketsService.list(
          'size=100&sort=name,asc'
        );
        setTicketTypes(response.content || []);
      } catch (error) {
        console.error('Erro ao buscar tipos de ticket', error);
      }
    };
    getTypeTickets();
  }, []);

  useEffect(() => {
    if (Object.keys(data).length != 0) {
      setBody(data);
      setDefaultSubject(data.defaultSubject || '');
      setDefaultDescription(data.defaultDescription || '');
      setSelectedTypeTicket(data.typeTicket ? data.typeTicket.id : '');
      setTicketUrgency(data.urgency || 'LOW');
      setTicketImpact(data.impact || 'LOW');
      setTicketPriority(data.ticketPriority || 'PLANNED');
      setValidated(false);
    }
  }, [data]);

  useEffect(() => {
    if (showModal) {
      // Re-populate from data when modal opens to ensure fresh state if data changed
      if (Object.keys(data).length != 0) {
        setBody(data);
        setDefaultSubject(data.defaultSubject || '');
        setDefaultDescription(data.defaultDescription || '');
        setSelectedTypeTicket(data.typeTicket ? data.typeTicket.id : '');
        setTicketUrgency(data.urgency || 'LOW');
        setTicketImpact(data.impact || 'LOW');
        setTicketPriority(data.ticketPriority || 'PLANNED');
      }
      setValidated(false);
    }
  }, [showModal, data]);

  useEffect(() => {
    const priority = calculatePriority(ticketUrgency, ticketImpact);
    setTicketPriority(priority || 'PLANNED');
  }, [ticketUrgency, ticketImpact]);

  const onSubmit = e => {
    e.preventDefault();
    if (form.current.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
    } else {
      setValidated(false);
      handleSubmit({
        name: body.name,
        inactive: body.inactive,
        defaultSubject,
        defaultDescription,
        typeTicket: selectedTypeTicket ? { id: selectedTypeTicket } : null,
        urgency: ticketUrgency,
        impact: ticketImpact,
        ticketPriority: ticketPriority || 'PLANNED'
      });
      // Reset is handled by parent or by closing modal usually, but we can clear local state if needed.
      // However, usually edit modal keeps state until closed or new data comes in.
    }
  };

  const priorityProps = getPriorityProps(ticketPriority);

  const urgencyOptions = [
    {
      value: 'LOW',
      label: 'Baixa',
      description: 'Problema causa pouco ou nenhum impacto no trabalho'
    },
    {
      value: 'MEDIUM',
      label: 'Média',
      description: 'Problema afeta trabalho, mas há alternativas'
    },
    {
      value: 'HIGH',
      label: 'Alta',
      description: 'Problema impede trabalho imediatamente'
    }
  ];

  const impactOptions = [
    {
      value: 'LOW',
      label: 'Baixo',
      description: 'Afeta apenas um usuário ou dispositivo.'
    },
    {
      value: 'MEDIUM',
      label: 'Médio',
      description: 'Afeta um setor ou grupo de pessoas.'
    },
    { value: 'HIGH', label: 'Alto', description: 'Afeta toda a empresa.' }
  ];

  return (
    <Modal show={showModal} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Editar subcategoria</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Row>
            <Col md={8}>
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label>
                  Nome <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="Nome"
                  autoFocus
                  value={body.name}
                  onChange={e =>
                    setBody(prevBody => ({ ...prevBody, name: e.target.value }))
                  }
                />
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Insira um nome.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Label>Status</Form.Label>
              <Form.Group>
                <Form.Switch
                  label={body.inactive ? 'Inativo' : 'Ativo'}
                  checked={!body.inactive}
                  onChange={e =>
                    setBody(prevBody => ({
                      ...prevBody,
                      inactive: !e.target.checked
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Assunto Padrão</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Assunto padrão do ticket"
                  value={defaultSubject}
                  onChange={e => setDefaultSubject(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Descrição Padrão</Form.Label>
                <Editor
                  data={defaultDescription}
                  value={setDefaultDescription}
                  style={{ height: '200px', marginBottom: '40px' }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Ticket</Form.Label>
                <Form.Select
                  value={selectedTypeTicket}
                  onChange={e => setSelectedTypeTicket(e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {ticketTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Urgência</Form.Label>
                <Dropdown onSelect={k => setTicketUrgency(k)}>
                  <Dropdown.Toggle as={CustomSelectToggle}>
                    {(() => {
                      const selected = urgencyOptions.find(
                        o => o.value === ticketUrgency
                      );
                      return selected ? (
                        <div className="d-flex flex-column text-start">
                          <span className="fw-medium text-900">
                            {selected.label}
                          </span>
                          {/* <span className="text-secondary fs-11 text-wrap lh-1 mt-1">
                            {selected.description}
                          </span> */}
                        </div>
                      ) : (
                        'Selecione'
                      );
                    })()}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {urgencyOptions.map(option => (
                      <Dropdown.Item
                        key={option.value}
                        eventKey={option.value}
                        active={ticketUrgency === option.value}
                        className="py-2"
                      >
                        <div className="d-flex flex-column text-start">
                          <span className="fw-medium text-900">
                            {option.label}
                          </span>
                          <span className="text-secondary fs-11 text-wrap lh-1 mt-1">
                            {option.description}
                          </span>
                        </div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Impacto</Form.Label>
                <Dropdown onSelect={k => setTicketImpact(k)}>
                  <Dropdown.Toggle as={CustomSelectToggle}>
                    {(() => {
                      const selected = impactOptions.find(
                        o => o.value === ticketImpact
                      );
                      return selected ? (
                        <div className="d-flex flex-column text-start">
                          <span className="fw-medium text-900">
                            {selected.label}
                          </span>
                          {/* <span className="text-secondary fs-11 text-wrap lh-1 mt-1">
                            {selected.description}
                          </span> */}
                        </div>
                      ) : (
                        'Selecione'
                      );
                    })()}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {impactOptions.map(option => (
                      <Dropdown.Item
                        key={option.value}
                        eventKey={option.value}
                        active={ticketImpact === option.value}
                        className="py-2"
                      >
                        <div className="d-flex flex-column text-start">
                          <span className="fw-medium text-900">
                            {option.label}
                          </span>
                          <span className="text-secondary fs-11 text-wrap lh-1 mt-1">
                            {option.description}
                          </span>
                        </div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group className="mb-3">
                <Form.Label>Prioridade</Form.Label>
                <div className="d-flex align-items-center gap-2 mt-2">
                  <div
                    style={{
                      '--falcon-circle-progress-bar': priorityProps.percentage
                    }}
                  >
                    <svg
                      className="circle-progress-svg"
                      width="26"
                      height="26"
                      viewBox="0 0 120 120"
                    >
                      <circle
                        className="progress-bar-rail"
                        cx="60"
                        cy="60"
                        r="54"
                        fill="none"
                        strokeLinecap="round"
                        strokeWidth="12"
                      ></circle>
                      <circle
                        className="progress-bar-top"
                        cx="60"
                        cy="60"
                        r="54"
                        fill="none"
                        strokeLinecap="round"
                        stroke={priorityProps.bg}
                        strokeWidth="12"
                      ></circle>
                    </svg>
                  </div>
                  <h6 className="mb-0 text-700">{priorityProps.text}</h6>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          Fechar
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubcategoryEditModal;
